import { BOOTSTRAP_QUERY_STALE_TIME, queryClient } from '@/utils/reactQueryClient';
import {
	BillingMethod,
	createStripeController,
	createSubscriptionController,
	createTenantBillingController,
} from '@saas/api-client';
import { globalAuthRequestFunction } from '../http/request';
import { makeMutation, makeQuery } from '../http/queryUtils';

export type CardEntity = NonNullable<BillingMethod['card']>;

// ---------------------------------------------------------------------------
// Keys factory
// ---------------------------------------------------------------------------
type StripeCtrl = ReturnType<typeof createStripeController>;
type SubscriptionCtrl = ReturnType<typeof createSubscriptionController>;
type TenantBillingCtrl = ReturnType<typeof createTenantBillingController>;

export const subscriptionServiceKeys = {
	base: 'SUBSCRIPTION',
	entitlement: (params: Parameters<TenantBillingCtrl['getEntitlement']>[0]) =>
		[subscriptionServiceKeys.base, 'entitlement', params] as const,
	billingDetails: (params: Parameters<StripeCtrl['getCustomerDetails']>[0]) =>
		[subscriptionServiceKeys.base, 'billingDetails', params] as const,
	trialPossible: (params: Parameters<SubscriptionCtrl['isTrialPossible']>[0]) =>
		[subscriptionServiceKeys.base, 'trialPossible', params] as const,
	paymentMethods: (params: Parameters<StripeCtrl['hasBilling']>[0]) =>
		[subscriptionServiceKeys.base, 'paymentMethods', params] as const,
	usage: (params: Parameters<SubscriptionCtrl['getUsage']>[0]) =>
		[subscriptionServiceKeys.base, 'usage', params] as const,
	billingHistory: (params: Parameters<StripeCtrl['getBillingHistory']>[0]) =>
		[subscriptionServiceKeys.base, 'billingHistory', params] as const,
	userBilling: (params: Parameters<StripeCtrl['hasBilling']>[0]) =>
		[subscriptionServiceKeys.base, 'userBilling', params] as const,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const dispatchHydrate = () => {
	window.dispatchEvent(new CustomEvent('HYDRATE', { detail: {} }));
};

// ---------------------------------------------------------------------------
// Invalidation map
// ---------------------------------------------------------------------------
export const invalidateSubscriptionQueries = {
	all: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base] }),
	entitlement: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base, 'entitlement'] }),
	billingDetails: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base, 'billingDetails'] }),
	trialPossible: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base, 'trialPossible'] }),
	paymentMethods: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base, 'paymentMethods'] }),
	usage: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base, 'usage'] }),
	billingHistory: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base, 'billingHistory'] }),
	userBilling: () => queryClient.invalidateQueries({ queryKey: [subscriptionServiceKeys.base, 'userBilling'] }),
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
const useGetBillingAddressDetails = makeQuery({
	queryFunction: createStripeController(globalAuthRequestFunction).getCustomerDetails,
	queryKeyFactory: (params) => [...subscriptionServiceKeys.billingDetails(params)],
});

const useIsTrialPossible = makeQuery({
	queryFunction: createSubscriptionController(globalAuthRequestFunction).isTrialPossible,
	queryKeyFactory: (params) => [...subscriptionServiceKeys.trialPossible(params)],
});

const useGetPaymentMethods = makeQuery({
	queryFunction: createStripeController(globalAuthRequestFunction).hasBilling,
	queryKeyFactory: (params) => [...subscriptionServiceKeys.paymentMethods(params)],
});

// The topbar plan pill reads usage on every authenticated boot, so it joins the t=0 bootstrap
// prefetch and pins the bootstrap staleness. Every addon/plan mutation invalidates it explicitly.
const useGetUsage = makeQuery({
	queryFunction: createSubscriptionController(globalAuthRequestFunction).getUsage,
	queryKeyFactory: (params) => [...subscriptionServiceKeys.usage(params)],
	defaultOptions: { staleTime: BOOTSTRAP_QUERY_STALE_TIME },
});

const useGetBillingHistory = makeQuery({
	queryFunction: createStripeController(globalAuthRequestFunction).getBillingHistory,
	queryKeyFactory: (params) => [...subscriptionServiceKeys.billingHistory(params)],
});

const useGetUserBilling = makeQuery({
	queryFunction: createStripeController(globalAuthRequestFunction).hasBilling,
	queryKeyFactory: (params) => [...subscriptionServiceKeys.userBilling(params)],
});

// The resolved entitlement (addon/trial/tier state) the pricing and add-ons pages render. A purchase
// invalidates this so gated features re-resolve in-session without a reload (PRD1 AC1/AC2).
const useGetEntitlement = makeQuery({
	queryFunction: createTenantBillingController(globalAuthRequestFunction).getEntitlement,
	queryKeyFactory: (params) => [...subscriptionServiceKeys.entitlement(params)],
});

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
const useUpdateBillingInfo = makeMutation({
	mutationFunction: createStripeController(globalAuthRequestFunction).updateCustomerDetails,
	onSuccess: () => {
		invalidateSubscriptionQueries.billingDetails();
	},
});

const useCheckPaymentSession = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).checkSession,
});

const useUpgradeWithSession = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).upgradeWithSession,
});

const useUpgrade = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).upgradeSubscription,
	onSuccess: () => {
		invalidateSubscriptionQueries.all();
		dispatchHydrate();
	},
});

const useStartTrial = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).startTrial,
	onSuccess: () => {
		invalidateSubscriptionQueries.all();
		dispatchHydrate();
	},
});

const useGetPortalURL = makeMutation({
	mutationFunction: createStripeController(globalAuthRequestFunction).getPortalLink,
});

const useCancelSubscription = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).cancelSubscription,
	onSuccess: () => {
		invalidateSubscriptionQueries.all();
		dispatchHydrate();
	},
});

const usePurchaseAddOnStorage = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).purchaseStorageAddon,
	onSuccess: () => {
		invalidateSubscriptionQueries.usage();
		dispatchHydrate();
	},
});

const useRemoveAddOnStorage = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).removeStorageAddon,
	onSuccess: () => {
		invalidateSubscriptionQueries.usage();
		dispatchHydrate();
	},
});

const useUpdateStorageAddonQuantity = makeMutation({
	mutationFunction: createSubscriptionController(globalAuthRequestFunction).updateStorageQuantity,
	onSuccess: () => {
		invalidateSubscriptionQueries.usage();
		dispatchHydrate();
	},
});

// A capability purchase/removal re-resolves the entitlement and usage so gated features flip in-session.
const invalidateEntitlementScopedQueries = () => {
	invalidateSubscriptionQueries.entitlement();
	invalidateSubscriptionQueries.usage();
	dispatchHydrate();
};

const useSubscriptionCheckout = makeMutation({
	mutationFunction: createTenantBillingController(globalAuthRequestFunction).subscriptionCheckout,
});

const useAddonCheckout = makeMutation({
	mutationFunction: createTenantBillingController(globalAuthRequestFunction).addonCheckout,
	onSuccess: invalidateEntitlementScopedQueries,
});

const useRemoveAddon = makeMutation({
	mutationFunction: createTenantBillingController(globalAuthRequestFunction).removeAddon,
	onSuccess: invalidateEntitlementScopedQueries,
});

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const subscriptionService = {
	useUpgrade,
	useCancelSubscription,
	useGetPaymentMethods,
	useGetPortalURL,
	useGetUsage,
	useIsTrialPossible,
	useStartTrial,
	useUpgradeWithSession,
	useCheckPaymentSession,
	useUpdateBillingInfo,
	useGetBillingAddressDetails,
	usePurchaseAddOnStorage,
	useRemoveAddOnStorage,
	useUpdateStorageAddonQuantity,
	useGetBillingHistory,
	useGetUserBilling,
	useGetEntitlement,
	useSubscriptionCheckout,
	useAddonCheckout,
	useRemoveAddon,
};
